<template>
  <div class="container">
    <van-sticky :offset-top="videoHeight + 70">
      <div class="tabs-title">
        <div class="tab" :class="{ active: activeTab === 0 }" @click="handleTabClick(0)">{{ $t('livePage.highlights') }}</div>
        <div class="tab" :class="{ active: activeTab === 1 }" @click="handleTabClick(1)">{{ $t('livePage.commentTab') }}</div>
      </div>
    </van-sticky>
    <!-- 视频集锦 -->
    <div class="box" ref="highlightsBox">
      <div class="video-list">
        <div 
          v-for="item in cols" 
          :key="item.videoId || item.id" 
          class="video-item" 
          :class="{ active: getItemVideoId(item) === selectedVideoId }"
          @click="handleVideoItemClick(item)"
        >
          <div class="video-item-title">{{ item.title }}</div>
          <img v-if="getItemVideoId(item) === selectedVideoId" src="@/assets/play.gif" class="play">
        </div>
      </div>
      <div class="live-tabs">
        <div v-for="(item,index) in livesData" :key="item.title + index" class="live-tab" :class="{ active: index === activeLiveTabIndex }" @click="activeLiveTabIndex = index">
          {{ liveTabTitle(item) }}
          <div class="live-tab-count">{{ item.list.length }}</div>
        </div>
      </div>
      <div class="live-list" v-if="livesData[activeLiveTabIndex]?.list.length > 0">
        <div 
          v-for="item in livesData[activeLiveTabIndex].list" 
          :key="item.id" 
          class="live-item"
          :class="{ active: getItemVideoId(item) === selectedVideoId }"
          @click="handleLiveItemClick(item)"
        >
          <div class="cover">
            <img :src="item.poster" />
          </div>
          <div class="info">
            <div class="title overflow-text-tow">{{ item.title }}</div>
            <div v-if="getItemVideoId(item) === selectedVideoId" class="play">
              <img src="@/assets/play.gif"><span>{{ $t('livePage.playing') }}</span>
            </div>
            <div class="count" v-else>
              <van-icon name="eye-o" />
              {{ item.pvCount }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty">
        {{ $t('livePage.noVideo') }}
      </div>
    </div>
    <!-- 评论 -->
    <div class="comment-box" ref="commentBox">
      <div class="comment-title">{{ $t('livePage.allComments') }} <span>{{ commentList.length }}</span></div>
      <div class="comment-list" v-if="commentList.length > 0">
        <div v-for="item in commentList" :key="item.id" class="comment-item">
          <div class="user-icon">
            <img :src="item.userIcon" />
          </div>
          <div class="comment-content">
            <div class="user-name">{{ item.userName }}</div>
            <div class="comment-text overflow-text-tow">{{ item.commentText }}</div>
          </div>
          <div class="comment-time">{{ item.commentTime }}</div>
        </div>
      </div>
      <div v-else class="empty">
        {{ $t('livePage.noComment') }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Sticky } from 'vant'

/** 与接口 tags 区分，「全部」Tab 专用标记 */
const ALL_TAB_KEY = '__ALL__'

Vue.use(Sticky)
export default {
  props: {
    cols: {
      type: Array,
      default: () => []
    },
    lives: {
      type: Array,
      default: () => []
    },
    activeVideoId: {
      type: String,
      default: ''
    },
    commentList: {
      type: Array,
      default: () => []
    },
    videoHeight: {
      type: Number,
      default: 0
    },
    /** interactEnabled=1 时允许点赞；评论 Tab 另看 commentAllowed */
    interactionAllowed: {
      type: Boolean,
      default: true
    },
    /** interactEnabled=1 且 commentEnabled=1 时才允许评论 */
    commentAllowed: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    activeVideoId: {
      handler(newVal) {
        if (newVal) this.selectedVideoId = newVal
      }
    },
    cols: {
      handler(newVal) {
        if (newVal.length > 0 && this.selectedVideoId === '' && !this.activeVideoId) {
          this.selectedVideoId = this.getItemVideoId(newVal[0])
        }
      },
      deep: true
    },
    lives: {
      handler(newVal) {
        const list = [
          {
            title: ALL_TAB_KEY,
            list: newVal
          }
        ]
        newVal.forEach(item => {
          const index = list.findIndex(a => a.title === item.tags)
          if (index === -1) {
            list.push({
              title: item.tags,
              list: [item]
            })
          } else {
            list[index].list.push(item)
          }
        })
        this.livesData = list
      },
      deep: true
    }
  },
  data() {
    return {
      activeTab: 0, // 集锦tab
      selectedVideoId: '', // 统一选中的视频id（用于集锦和打点列表的选中状态）
      livesData: [], // 打点列表数据
      activeLiveTabIndex: 0 // 打点tab索引
    };
  },
  methods: {
    liveTabTitle(item) {
      if (item && item.title === ALL_TAB_KEY) return this.$t('livePage.all')
      return (item && item.title) || ''
    },
    /**
     * 处理 tab 点击，切换 tab 并滚动到对应区域
     * @param {Number} index - tab 索引（0: 精彩集锦, 1: 评论）
     */
    handleTabClick(index) {
      if (index === 1) {
        if (this.interactionAllowed === false) {
          this.$emit('interaction-not-allowed')
          return
        }
        if (this.commentAllowed === false) {
          this.$emit('comment-not-allowed')
          return
        }
      }
      this.activeTab = index

      // 使用 nextTick 确保 DOM 更新后再滚动
      this.$nextTick(() => {
        this.scrollToTabContent(index)
      })
    },
    /**
     * 滚动到对应的 tab 内容区域
     * @param {Number} index - tab 索引（0: 精彩集锦, 1: 评论）
     */
    scrollToTabContent(index) {
      let targetElement = null
      
      if (index === 0) {
        // 滚动到精彩集锦区域
        targetElement = this.$refs.highlightsBox
      } else if (index === 1) {
        // 滚动到评论区域
        targetElement = this.$refs.commentBox
      }
      
      if (targetElement) {
        // 获取目标元素距离页面顶部的距离
        const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset - 111
        
        // 动态获取队伍信息区域的高度
        // const teamInfoElement = document.querySelector('.team-info')
        // const teamInfoHeight = teamInfoElement ? teamInfoElement.offsetHeight : 70
        
        // 计算固定顶部元素的总高度
        // 视频高度（固定顶部） + 队伍信息高度
        const fixedHeight = this.videoHeight
        // 计算滚动位置（减去固定元素高度，确保内容不被遮挡）
        const scrollPosition = elementTop - fixedHeight
        
        // 平滑滚动到目标位置
        window.scrollTo({
          top: Math.max(0, scrollPosition), // 确保不会滚动到负值
          behavior: 'smooth'
        })
      }
    },
    /**
     * 获取项的 videoId（统一处理 videoId 和 id）
     * @param {Object} item - 数据项
     * @returns {String} videoId
     */
    getItemVideoId(item) {
      return item.videoId || item.id || ''
    },
    /**
     * 处理集锦项点击
     * @param {Object} item - 集锦项数据
     */
    handleVideoItemClick(item) {
      // 更新统一选中状态
      this.selectedVideoId = this.getItemVideoId(item)
      
      // 构造视频数据并传递给父组件
      const videoData = {
        videoUrl: item.url,
        videoId: this.getItemVideoId(item),
        poster: item.poster,
        streamType: item.streamType || 'mp4',
        title: item.title,
        alreadyLike: item.alreadyLike,
        objType: item.objType
      }
      
      this.$emit('videoUrl', videoData)
      console.log('点击集锦项:', videoData)
    },
    /**
     * 处理打点项点击
     * @param {Object} item - 打点项数据
     */
    handleLiveItemClick(item) {
      // 更新统一选中状态
      this.selectedVideoId = this.getItemVideoId(item)
      
      // 构造视频数据并传递给父组件
      const videoData = {
        videoUrl: item.url,
        videoId: this.getItemVideoId(item),
        poster: item.poster,
        streamType: item.streamType || 'mp4',
        title: item.title,
        alreadyLike: item.alreadyLike,
        objType: item.objType
      }
      
      this.$emit('videoUrl', videoData)
      console.log('点击打点项:', videoData)
    },
    logCols(){
      console.log(this.cols);
      console.log('选中的视频ID:', this.selectedVideoId);
      console.log(this.livesData);
      
    }
  },
};
</script>

<style scoped lang="scss">
.container{
  .tabs-title{
    background-color: #EEEEF1;
    display: flex;
    align-items: center;
    padding: 10px 15px;
    gap: 40px;
    .tab{
      font-size: 15px;
      color: #606266;
    }
    .active{
      color: #000;
      font-weight: 500;
    }
  }
  .video-list{
    padding: 13px 15px;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    background-color: #fff;
    .video-item{
      height: 48px;
      border-radius: 2px;
      background: #F7F8FA;
      text-align: center;
      line-height: 48px;
      color: #606266;
      font-size: 14px;
      box-sizing: border-box;
      padding: 0 10px;
      white-space: nowrap;
      cursor: pointer;
      transition: all 0.2s;
      position: relative;
      &:hover {
        background: rgba($color: #D54F3F, $alpha: .1);
      }
      .video-item-title{
        min-width: 88px;
      }
      .play{
        position: absolute;
        right: 6px;
        bottom: 3px;
        width: 14px;
        height: 15px;
      }
    }
    .active{
      background: rgba($color: #D54F3F, $alpha: .2);
      color: #D54F3F;
      font-weight: 500;
    }
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .live-tabs{
    padding-left: 15px;
    display: flex;
    gap: 40px;
    overflow-x: auto;
    background-color: #fff;
    .live-tab{
      font-size: 13px;
      color: #606266;
      height: 40px;
      line-height: 40px;
      overflow: hidden;
      .live-tab-count{
        display: inline-block;
        font-size: 10px;
        color: #8E9196;
        margin-left: 2px;
        transform: translateY(-4px);
      }
    }
    .active{
      color: #D54F3F;
      font-weight: 500;
      .live-tab-count{
        color: #D54F3F;
      }
    }
  }
  .live-list{
    background-color: #fff;
    .live-item{
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      padding: 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.2s;
      &.active {
        .info .title {
          color: #D54F3F;
        }
      }
      .cover{
        width: 106px;
        min-width: 106px;
        height: 59px;
        border-radius: 4px;
        overflow: hidden;
        margin-right: 10px;
        img{
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
      .info{
        height: 59px;
        padding-right: 12px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        .title{
          font-size: 14px;
          color: #1D2129;
        }
        .count{
          font-size: 12px;
          color: #8E9196;
          margin-top: 4px;
        }
        .play{
          background-color: rgba($color: #D54F3F, $alpha: .2);
          width: 55px;
          height: 19px;
          border-radius: 2px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #D54F3F;
           font-size: 11px;
          img{
            width: 12px;
            height: 14px;
            margin-right: 4px;
          }
          span{
           transform: translateY(1px);
          }
        }
      }
    }
  }
  .comment-box{
    background-color: #fff;
    margin-top: 10px;
    padding: 17px 22px;
    .comment-title{
      font-size: 13px;
      color: #1D2129;
      padding: 15px 0;
      span{
        margin-left: 9px;
      }
    }
    .comment-list{
      .comment-item{
        display: flex;
        align-items: center;
        margin-bottom: 38px;
        .user-icon{
          width: 28px;
          height: 28px;
          background: #D8D8D8;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 8px;
          img{
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
        .comment-content{
          flex: 1;
          transform: translateY(12px);
          margin-right: 18px;
          .user-name{
            font-size: 13px;
            color: #8E9196;
            margin-bottom: 7px;
          }
          .comment-text{
            font-size: 14px;
            color: #000;
          }
        }
        .comment-time{
          color: #8E9196;
          font-size: 13px;
        }
      }
    }
  }
  .empty{
    text-align: center;
    background-color: #fff;
    padding: 25px 0;
    color: #8E9196;
    font-size: 14px;
  }
}
</style>
